import type { FileTreeNode } from "@promptlayer/ui";

import type { EditorTreeNode } from "../types";

import { stripRootPathPrefix } from "./path-utils";

import type { SkillCollectionTreeNode } from "@/queries/skill-collections";

type MutableTreeNode =
  | { type: "file"; name: string; path: string }
  | {
      type: "folder";
      name: string;
      path: string;
      children: Map<string, MutableTreeNode>;
    };

interface CollectedServerTree {
  filePaths: string[];
  folderPaths: string[];
}

interface RelativeTreePaths {
  filePaths: string[];
  folderPaths: string[];
}

const sortNodes = (a: MutableTreeNode, b: MutableTreeNode) => {
  if (a.type !== b.type) {
    return a.type === "folder" ? -1 : 1;
  }

  return a.name.localeCompare(b.name);
};

const ensureFolderNode = (root: Map<string, MutableTreeNode>, folderPath: string) => {
  const parts = folderPath.split("/").filter(Boolean);
  let current = root;

  parts.forEach((part, index) => {
    const currentPath = parts.slice(0, index + 1).join("/");
    const existing = current.get(part);

    if (existing?.type === "folder") {
      current = existing.children;
      return;
    }

    const folderNode: MutableTreeNode = {
      type: "folder",
      name: part,
      path: currentPath,
      children: new Map<string, MutableTreeNode>(),
    };

    current.set(part, folderNode);
    current = folderNode.children;
  });
};

const addFileNode = (root: Map<string, MutableTreeNode>, filePath: string) => {
  const parts = filePath.split("/").filter(Boolean);
  const fileName = parts[parts.length - 1];

  if (!fileName) {
    return;
  }

  const parentPath = parts.slice(0, -1).join("/");
  if (parentPath) {
    ensureFolderNode(root, parentPath);
  }

  let current = root;
  parts.slice(0, -1).forEach(part => {
    const folder = current.get(part);
    if (folder?.type === "folder") {
      current = folder.children;
    }
  });

  current.set(fileName, {
    type: "file",
    name: fileName,
    path: filePath,
  });
};

const buildMutableTreeRoot = (filePaths: string[], folderPaths: string[]) => {
  const root = new Map<string, MutableTreeNode>();

  folderPaths
    .filter(Boolean)
    .sort((a, b) => a.split("/").length - b.split("/").length)
    .forEach(folderPath => ensureFolderNode(root, folderPath));

  filePaths
    .filter(Boolean)
    .sort()
    .forEach(filePath => addFileNode(root, filePath));

  return root;
};

const mapMutableRootToTree = <T extends EditorTreeNode | FileTreeNode>(
  nodes: Map<string, MutableTreeNode>,
): T[] =>
  Array.from(nodes.values())
    .sort(sortNodes)
    .map(node => {
      if (node.type === "file") {
        return { ...node } as T;
      }

      return {
        type: "folder",
        name: node.name,
        path: node.path,
        children: mapMutableRootToTree<T>(node.children),
      } as unknown as T;
    });

const mapMutableRootToServerTree = (
  nodes: Map<string, MutableTreeNode>,
): SkillCollectionTreeNode[] =>
  Array.from(nodes.values())
    .sort(sortNodes)
    .map(node => {
      if (node.type === "file") {
        return {
          type: "file",
          id: 0,
          name: node.name,
          path: node.path,
        };
      }

      return {
        type: "folder",
        name: node.name,
        path: node.path,
        children: mapMutableRootToServerTree(node.children),
      };
    });

const normalizeTreeEntryPath = (path: string, rootPath?: string | null) => {
  const normalizedPath = stripRootPathPrefix(path, rootPath);
  return normalizedPath.endsWith("/") ? normalizedPath.slice(0, -1) : normalizedPath;
};

const toRelativeTreePaths = (paths: string[], rootPath?: string | null): RelativeTreePaths =>
  paths.reduce<RelativeTreePaths>(
    (acc, path) => {
      const normalizedPath = normalizeTreeEntryPath(path, rootPath);
      if (!normalizedPath) {
        return acc;
      }

      if (path.endsWith("/")) {
        acc.folderPaths.push(normalizedPath);
      } else {
        acc.filePaths.push(normalizedPath);
      }

      return acc;
    },
    { filePaths: [], folderPaths: [] },
  );

export const collectServerTree = (
  nodes: SkillCollectionTreeNode[],
  acc: CollectedServerTree = { filePaths: [], folderPaths: [] },
): CollectedServerTree => {
  nodes.forEach(node => {
    if (node.type === "file") {
      acc.filePaths.push(node.path);
      return;
    }

    acc.folderPaths.push(node.path);
    collectServerTree(node.children, acc);
  });

  return acc;
};

export const getNormalizedServerTreePaths = (
  serverTree: SkillCollectionTreeNode[],
  rootPath?: string | null,
) => {
  const treePaths = collectServerTree(serverTree);
  return toRelativeTreePaths(
    [...treePaths.filePaths, ...treePaths.folderPaths.map(path => `${path}/`)],
    rootPath,
  );
};

export const buildEditorTree = (filePaths: string[], folderPaths: string[]): EditorTreeNode[] =>
  mapMutableRootToTree<EditorTreeNode>(buildMutableTreeRoot(filePaths, folderPaths));

export const buildOverviewTree = (filePaths: string[], rootPath: string): FileTreeNode[] => {
  const { filePaths: relativeFilePaths, folderPaths } = toRelativeTreePaths(filePaths, rootPath);
  return mapMutableRootToTree<FileTreeNode>(buildMutableTreeRoot(relativeFilePaths, folderPaths));
};

export const buildServerTreeFromVersionSnapshot = (
  files: Record<string, string>,
  filePaths?: string[] | null,
): SkillCollectionTreeNode[] => {
  const { folderPaths } = toRelativeTreePaths(filePaths ?? []);
  return mapMutableRootToServerTree(buildMutableTreeRoot(Object.keys(files), folderPaths));
};
